import React, { FC, useRef, useEffect, useState, ReactElement } from 'react';
import { useAnimation } from 'motion/react';
import useClientSideMediaQuery from 'hooks/use-client-side-media-query';
import { Swiper, SwiperSlide } from 'swiper/react';
import Button, { ButtonProps } from '../Button';
import TextButton, { TextButtonProps } from '../TextButton';
import MuxPlayer from 'components/reusable/MuxPlayer/mux-player';
import 'swiper/css';
import 'swiper/css/pagination';
import {
  Parallax,
  Mousewheel,
  Pagination,
  EffectCreative,
} from 'swiper/modules';
import {
  HeroSwiperContainer,
  HeroHead,
  HeroTitle,
  HeroSubhead,
  HeroActions,
  SlideContent,
  SlideText,
  SlideTextContainer,
  SlideImage,
  SlidePicture,
  SlideVideo,
  HeroPauseButtonContainer,
} from './hero.styled';
import { mqLargeMin } from 'styled/tools/media-queries';
import { buttonEventHandler } from 'lib/utils/button-event-handler';
import Label from '../Label';
import Icon from 'components/global/Icon/Primary';
import { formatTrademarks } from 'lib/utils/format-trademarks';

export type positions = 'top' | 'midtop' | 'center' | 'bottom';
export type alignments = 'left' | 'center';

export interface Slide {
  backgroundDesktop: { src: string; alt: string };
  backgroundMobile: { src: string; alt: string };
  videoDesktop?: { devPlaybackId: string; playbackId: string; alt: string };
  videoMobile?: { devPlaybackId: string; playbackId: string; alt: string };
  pill?: string;
  title: string;
  subhead?: string;
  position: positions;
  mobilePosition?: positions;
  alignment: alignments;
  primaryButton?: ButtonProps;
  secondaryButton?: TextButtonProps;
  titleVariant?: 'catalog';
  hideFilter?: boolean;
  textColor?: 'seedgreen' | 'snowwhite';
  titleMobileFontSize?: string;
  mobileTitle?: string;
  mobileAlignment?: alignments;
  desktopMarginTop?: string;
  desktopVerticalCenter?: boolean;
  mobileImageFit?: 'cover' | 'contain';
  mobileBackgroundColor?: string;
  desktopImageFit?: 'cover' | 'contain';
  desktopBackgroundColor?: string;
}

interface HeroProps {
  slides: Slide[];
  label?: React.ReactNode;
  pillAndTitle?: ReactElement;
  imageAnimationControls?: ReturnType<typeof useAnimation>;
  config?: {
    height?: string;
    imageScale?: number;
  };
}

const onScrollDownVariants = {
  animate: {
    scale: 1,
    transition: {
      duration: 0.8,
      ease: 'easeOut' as const,
    },
  },
};

const onScrollUpVariants = {
  animate: {
    scale: 1,
    transition: {
      duration: 0.8,
      ease: 'easeOut' as const,
    },
  },
};

const Hero: FC<HeroProps> = ({
  slides,
  imageAnimationControls,
  config,
  label,
  pillAndTitle,
}) => {
  const isSmall = useClientSideMediaQuery(960);
  const [scrollDir, setScrollDir] = useState('');
  const imgRefs = useRef<HTMLImageElement[]>([]);
  const videoRefs = useRef<HTMLVideoElement[]>([]);
  const [isPlaying, setIsPlaying] = useState<boolean>(true);

  const handlePlayPauseClick = () => {
    if (videoRefs.current) {
      if (isPlaying) {
        videoRefs.current[0]?.pause();
        setIsPlaying(false);
        buttonEventHandler('Pause video', 'Hero');
      } else {
        videoRefs.current[0]?.play();
        setIsPlaying(true);
        buttonEventHandler('Play video', 'Hero');
      }
    }
  };

  useEffect(() => {
    const threshold = 0;
    let lastScrollY = window.pageYOffset;
    let ticking = false;

    const updateScrollDir = () => {
      const scrollY = window.pageYOffset;

      if (Math.abs(scrollY - lastScrollY) < threshold) {
        ticking = false;
        return;
      }
      setScrollDir(scrollY > lastScrollY ? 'down' : 'up');
      lastScrollY = scrollY > 0 ? scrollY : 0;
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateScrollDir);
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll);

    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // helps us manually set linebreaks with <br /> in the string
  const renderWithBreaks = (text: string) =>
    text.split('<br />').map((part, i) => (
      <React.Fragment key={i}>
        {part}
        {i < text.split('<br />').length - 1 && <br />}
      </React.Fragment>
    ));

  return (
    <HeroSwiperContainer>
      <Swiper
        effect={'creative'}
        creativeEffect={{
          prev: {
            translate: [0, '-20%', -1],
          },
          next: {
            translate: [0, '100%', 0],
          },
        }}
        speed={400}
        parallax={true}
        direction='vertical'
        slidesPerView={1}
        pagination={{
          clickable: true,
        }}
        mousewheel={{
          forceToAxis: true,
          sensitivity: 1,
          releaseOnEdges: true,
        }}
        touchReleaseOnEdges={true}
        threshold={5}
        modules={[EffectCreative, Parallax, Mousewheel, Pagination]}
      >
        {slides.map((slide: Slide, index: number) => {
          const media = isSmall
            ? slide.backgroundMobile
            : slide.backgroundDesktop;

          const videoMedia = isSmall ? slide.videoMobile : slide.videoDesktop;
          const videoPlaybackId =
            process.env.NEXT_PUBLIC_STAGING === 'true'
              ? videoMedia?.devPlaybackId
              : videoMedia?.playbackId;

          const currentAlignment =
            isSmall && slide.mobileAlignment
              ? slide.mobileAlignment
              : slide.alignment;
          const currentTitle =
            isSmall && slide.mobileTitle ? slide.mobileTitle : slide.title;

          return (
            <SwiperSlide key={index}>
              <SlideContent
                id={`slide-content-${index}`}
                data-swiper-parallax='5%'
                position={slide.position}
                $mobilePosition={slide.mobilePosition}
                alignment={currentAlignment}
                className='content'
                $hideFilter={slide.hideFilter}
                $desktopVerticalCenter={slide.desktopVerticalCenter}
              >
                <SlideTextContainer
                  id='slide-text-container'
                  position={slide.position}
                  $mobilePosition={slide.mobilePosition}
                  $desktopVerticalCenter={slide.desktopVerticalCenter}
                  $titleVariant={slide.titleVariant}
                >
                  <SlideText
                    data-swiper-parallax-y='-168'
                    data-swiper-parallax-x='0'
                    data-swiper-parallax-duration='700'
                    alignment={currentAlignment}
                  >
                    {label && <Label size='small'>{label}</Label>}
                    {pillAndTitle && pillAndTitle}
                    <HeroHead alignment={currentAlignment}>
                      <HeroTitle
                        color={
                          slide?.textColor === 'seedgreen'
                            ? 'primary'
                            : 'inverse-primary'
                        }
                        as='h2'
                        data-pill={slide?.pill}
                        $titleMobileFontSize={slide?.titleMobileFontSize}
                        $desktopMarginTop={slide?.desktopMarginTop}
                      >
                        {renderWithBreaks(currentTitle)}
                        <br />
                      </HeroTitle>
                      {slide?.subhead && (
                        <HeroSubhead
                          color={
                            slide?.textColor === 'seedgreen'
                              ? 'primary'
                              : 'inverse-primary'
                          }
                        >
                          {formatTrademarks(slide?.subhead)}
                        </HeroSubhead>
                      )}
                    </HeroHead>

                    {(slide?.primaryButton || slide?.secondaryButton) && (
                      <HeroActions
                        alignment={currentAlignment}
                        position={slide.position}
                        $mobilePosition={slide.mobilePosition}
                      >
                        {slide?.primaryButton && (
                          <Button
                            animated={true}
                            color={slide?.primaryButton?.color || 'snowwhite'}
                            url={slide.primaryButton.url}
                            onClick={() =>
                              buttonEventHandler(
                                slide?.primaryButton?.label,
                                'Hero',
                              )
                            }
                          >
                            {formatTrademarks(
                              slide?.primaryButton?.label ?? '',
                              false,
                              '0.1rem',
                            )}
                          </Button>
                        )}
                        {slide?.secondaryButton && (
                          <TextButton
                            color={slide?.textColor || 'snowwhite'}
                            label={slide?.secondaryButton?.label}
                            url={slide.secondaryButton.url}
                            underline={true}
                            transform='lower'
                            hideArrow={slide?.secondaryButton?.hideArrow}
                            onClick={() =>
                              buttonEventHandler(
                                slide?.secondaryButton?.label,
                                'Hero',
                              )
                            }
                          />
                        )}
                      </HeroActions>
                    )}
                  </SlideText>
                </SlideTextContainer>

                {videoMedia && videoPlaybackId ? (
                  <HeroPauseButtonContainer>
                    <div>
                      <Button
                        color='frostedglass'
                        size='small'
                        onClick={handlePlayPauseClick}
                      >
                        {isPlaying ? (
                          <Icon
                            name='pause'
                            size='md'
                            className='pause-play-white'
                            aria-label='Pause'
                          />
                        ) : (
                          <Icon
                            name='play'
                            size='md'
                            className='pause-play-white'
                            aria-label='Play'
                          />
                        )}
                      </Button>
                    </div>
                  </HeroPauseButtonContainer>
                ) : null}

                {/* Render video if available, otherwise fall back to image */}
                {videoMedia && videoPlaybackId ? (
                  <SlideVideo
                    ref={(element: HTMLDivElement) => {
                      if (element) {
                        imgRefs.current[index] = element as any;
                      }
                    }}
                    data-swiper-parallax-y='-20%'
                    data-swiper-parallax-x='0'
                    data-swiper-parallax-duration='400'
                    animate={
                      scrollDir == 'down' || scrollDir == 'up'
                        ? 'animate'
                        : imageAnimationControls
                    }
                    variants={
                      scrollDir == 'down'
                        ? onScrollDownVariants
                        : scrollDir == 'up'
                          ? onScrollUpVariants
                          : undefined
                    }
                    initial={{ scale: 1.2 }}
                  >
                    <MuxPlayer
                      playbackId={videoPlaybackId}
                      autoPlay
                      muted
                      videoRefs={videoRefs}
                      isManuallyControlled={false}
                      loop
                      index={0}
                      lazy={false}
                      height='100%'
                      style={{
                        /*@ts-ignore*/
                        '--controls': 'none',
                        '--media-object-fit': 'cover',
                        '--media-object-position': 'center',
                      }}
                    />
                  </SlideVideo>
                ) : (
                  // <picture> lets the browser fetch only the image for its viewport; SSR can't know it
                  <SlidePicture>
                    <source
                      media={mqLargeMin}
                      srcSet={slide.backgroundDesktop.src}
                    />
                    <SlideImage
                      src={slide.backgroundMobile.src}
                      alt={media.alt ?? ''}
                      ref={(element: HTMLImageElement) => {
                        if (element) {
                          imgRefs.current[index] = element;
                        }
                      }}
                      data-swiper-parallax-y='-20%'
                      data-swiper-parallax-x='0'
                      data-swiper-parallax-duration='400'
                      animate={
                        scrollDir == 'down' || scrollDir == 'up'
                          ? 'animate'
                          : imageAnimationControls
                      }
                      variants={
                        scrollDir == 'down'
                          ? onScrollDownVariants
                          : scrollDir == 'up'
                            ? onScrollUpVariants
                            : undefined
                      }
                      initial={{ scale: config?.imageScale || 1 }}
                      $mobileImageFit={slide.mobileImageFit}
                      $mobileBackgroundColor={slide.mobileBackgroundColor}
                      $desktopImageFit={slide.desktopImageFit}
                      $desktopBackgroundColor={slide.desktopBackgroundColor}
                    />
                  </SlidePicture>
                )}
              </SlideContent>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </HeroSwiperContainer>
  );
};

export default Hero;
